import {
    PlusCircledIcon,
    UpdateIcon
} from '@radix-ui/react-icons';
import { useState } from 'react';
import { Button } from '../ui/button';
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "../ui/dialog";
import {
    Form,
    FormControl,
    FormDescription,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '../ui/form';
import { Input } from '../ui/input';
import { toast } from '../ui/use-toast';

import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import api from '../../lib/api';
import { DatePickerSingle } from '../datePickerSingle';
import SelectBunnyPopover from '../selectBunnyPopver';

function CreateNewLitterDialog({ fetchLitters, farm }) {

    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const navigate = useNavigate();

    const form = useForm();
    const [isLoading, setIsLoading] = useState(false);

    const [formData, setFormData] = useState({
        name: '',
        father: '',
        mother: '',
        birthDate: '',
        inbreeding: '',
        no_children: '',
    });

    // Submit
    const sendCreationRequest = (e) => {
        if (isLoading) return;

        e.preventDefault();
        setIsLoading(true);

        api.post(`https://skhrf-api.cmdigital.se/v1/litters/create`, {
            farmId: farm.id,
            ...formData
        })
            .then(response => {
                fetchLitters();
                form.reset({
                    name: '',
                    father: '',
                    mother: '',
                    birthDate: '',
                    inbreeding: '',
                    no_children: '',
                });
                setIsLoading(false);
                toast({
                    title: 'Kull skapad.',
                    description: `Kullen har skapats och lagts till i systemet.`,
                });
                setIsDialogOpen(false);
                navigate(`/litters/${response?.data?.litterId}`);
            })
            .catch(error => {
                toast({
                    variant: 'destructive',
                    title: 'Något gick fel.',
                    description: error?.response?.data?.error,
                });
                setIsLoading(false);
            });
    };

    const handleValueChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value,
        });
    };

    const handleDateChange = (date, name) => {
        if (!date) {
            return; // Early return if the date is null or undefined
        }

        setFormData(prevState => ({
            ...prevState,
            [name]: date.toLocaleString('sv').split(' ')[0], // Update the specific field (startDate, endDate, etc.)
        }));
    };

    return (
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
                <Button>
                    <PlusCircledIcon className='mr-3' />
                    Skapa ny kull
                </Button>
            </DialogTrigger>
            <DialogContent>
                <DialogHeader className='text-left'>
                    <DialogTitle>Skapa ny kull</DialogTitle>
                    <DialogDescription>Ange information för att skapa en ny kull.</DialogDescription>
                </DialogHeader>
                <Form {...form}>
                    <form onSubmit={sendCreationRequest} className="flex flex-col gap-6">
                        <FormField
                            control={form.control}
                            name="name"
                            render={({ field }) => (
                                <FormItem className='space-y-0 grid gap-2'>
                                    <FormLabel>Kullnamn</FormLabel>
                                    <FormControl>
                                        <Input
                                            {...field}
                                            value={formData.name}
                                            onChange={handleValueChange}
                                            placeholder='Högskolor i Sverige'
                                        />
                                    </FormControl>
                                    <FormDescription>
                                        Kullens namn behandlas också som kullens tema.
                                    </FormDescription>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="birthDate"
                            render={({ field }) => (
                                <FormItem className='space-y-0 grid gap-2'>
                                    <FormLabel>Kullens födelsedatum</FormLabel>
                                    <FormControl>
                                        <DatePickerSingle onDateChange={(date) => handleDateChange(date, field.name)} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <SelectBunnyPopover
                            form={form}
                            name="father"
                            label="Far"
                            gender="male"
                            allowDead={true}
                            onSelect={(bunnyId) => setFormData(prevState => ({ ...prevState, father: bunnyId }))}
                        />
                        <SelectBunnyPopover
                            form={form}
                            name="mother"
                            label="Mor"
                            gender="female"
                            allowDead={true}
                            onSelect={(bunnyId) => setFormData(prevState => ({ ...prevState, mother: bunnyId }))}
                        />
                        <FormField
                            control={form.control}
                            name="no_children"
                            render={({ field }) => (
                                <FormItem className='space-y-0 grid gap-2'>
                                    <FormLabel>Antal födda kaniner</FormLabel>
                                    <FormControl>
                                        <Input
                                            {...field}
                                            value={formData.no_children}
                                            onChange={handleValueChange}
                                            type='number'
                                            min='0'
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <div className='grid grid-cols-2 gap-3'>
                            <DialogClose asChild>
                                <Button type='button' variant='secondary'>Avbryt</Button>
                            </DialogClose>
                            <Button type="submit" disabled={isLoading}>
                                {isLoading ? <UpdateIcon className='loader-spinner' /> : <>Skapa kull</>}
                            </Button>
                        </div>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    );
};

export default CreateNewLitterDialog;
